import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import useStory from '@/hooks/useStory';
import { formatPublished } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';
import ArticleBody from './components/ArticleBody';
import RelatedStories from './components/RelatedStories';

export default function Article() {
  const { slug } = useParams();
  const { data: story, loading, error, reload } = useStory(slug);

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Header />
      <main>
        {loading && <div className="px-4 md:px-8 pt-32 pb-20"><LoadingBlock label="Loading story" /></div>}
        {error && <div className="px-4 md:px-8 pt-32 pb-20"><ErrorBlock message={error} onRetry={reload} /></div>}

        {!loading && !error && story && (
          <>
            <section className="relative h-[440px] md:h-[600px] flex items-end">
              <img src={story.image} alt={story.title} title={story.title} className="absolute inset-0 w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/45 to-foreground-950/25"></div>
              <div className="relative w-full px-4 md:px-8 pb-10 md:pb-16">
                <Link to="/news" className="gs-rise inline-flex items-center gap-2 text-background-200 hover:text-background-50 font-label font-semibold uppercase tracking-wider text-sm mb-4 cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>All stories
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="gs-rise inline-block bg-accent-500 text-foreground-950 font-label font-bold uppercase text-[11px] tracking-[0.18em] px-3 py-1.5 rounded-sm">
                    {story.category}{story.city ? ` \u00b7 ${story.city}` : ''}
                  </span>
                  {story.is_demo && <span className="gs-rise"><DemoBadge label="Sample story" tone="dark" /></span>}
                </div>
                <h1 className="gs-rise-2 mt-4 font-heading font-extrabold text-background-50 text-4xl md:text-6xl leading-[1.0] max-w-4xl">{story.title}</h1>
                <p className="gs-rise-3 mt-4 font-label uppercase tracking-widest text-[11px] md:text-xs text-background-200">
                  {story.author || 'GreenSide Newsroom'}{story.published_at ? ` \u00b7 ${formatPublished(story.published_at)}` : ''}{story.read_time ? ` \u00b7 ${story.read_time}` : ''}
                </p>
              </div>
            </section>

            <article className="px-4 md:px-8 py-12 md:py-16">
              <div className="max-w-3xl mx-auto">
                {story.excerpt && (
                  <p className="text-xl md:text-2xl text-foreground-700 leading-relaxed font-medium border-l-4 border-accent-500 pl-5 mb-9">{story.excerpt}</p>
                )}
                {story.body ? (
                  <ArticleBody body={story.body} />
                ) : (
                  <p className="text-foreground-600">The full story will be published here shortly.</p>
                )}

                {story.is_demo && (
                  <div className="mt-10 bg-secondary-50 border border-secondary-200 rounded-lg p-5 flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center text-secondary-700 shrink-0 mt-0.5"><i className="ri-flask-line"></i></span>
                    <p className="text-sm text-secondary-900">
                      <strong>Demonstration content.</strong> This sample article exists to show how a full GreenSide News story reads — layout, imagery, author line and related stories. It is not a real report.
                    </p>
                  </div>
                )}
              </div>
            </article>

            <RelatedStories category={story.category} excludeId={story.id} />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}